import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

import { resolveRowKeyForDataKey } from '@/utils/table-cell-value'

interface StackedBarComponentProps {
  data: Array<Record<string, any>>
  xAxisKey: string
  yAxisKeys: string[] // Múltiples series apiladas
  title?: string
  layout?: 'vertical' | 'horizontal'
  showGrid?: boolean
  colors?: string[]
  loading?: boolean
}

// Paleta de colores por defecto
const DEFAULT_COLORS = [
  '#10b981', // Verde
  '#3b82f6', // Azul
  '#f59e0b', // Naranja
  '#ef4444', // Rojo
  '#8b5cf6', // Morado
  '#14b8a6', // Teal
]

export function StackedBarComponent({
  data,
  xAxisKey,
  yAxisKeys,
  title,
  layout = 'horizontal',
  showGrid = true,
  colors = DEFAULT_COLORS,
  loading = false,
}: StackedBarComponentProps) {
  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center text-gray-500">
          No hay datos disponibles
        </div>
      </div>
    )
  }

  const sample = data[0] as Record<string, unknown>
  const resolvedX = resolveRowKeyForDataKey(sample, xAxisKey)
  const resolvedYKeys = yAxisKeys.map((k) => resolveRowKeyForDataKey(sample, k))

  // Transformar datos: claves resueltas coinciden con columnas del resultado SQL (alias)
  const chartData = data.map((item) => {
    const row: Record<string, any> = {
      [resolvedX]: item[resolvedX as keyof typeof item],
    }

    resolvedYKeys.forEach((resolvedKey, i) => {
      const origKey = yAxisKeys[i]
      const raw =
        item[resolvedKey as keyof typeof item] ??
        item[origKey as keyof typeof item]
      row[resolvedKey] = Number(raw) || 0
    })

    return row
  })

  // Calcular totales por fila
  const dataWithTotals = chartData.map((item) => {
    const total = resolvedYKeys.reduce((sum, key) => sum + (item[key] || 0), 0)
    return { ...item, _total: total }
  })

  // Custom tooltip con totales
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum: number, entry: any) => sum + entry.value, 0)

      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm text-gray-600">
              <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: entry.color }}></span>
              {entry.name}: <span className="font-medium">{entry.value.toLocaleString('es-ES')}</span>
            </p>
          ))}
          <div className="mt-2 pt-2 border-t border-gray-200">
            <p className="text-sm font-semibold text-gray-900">
              Total: {total.toLocaleString('es-ES')}
            </p>
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <div className="bg-white border rounded-lg p-6">
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}

      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={dataWithTotals}
          layout={layout === 'vertical' ? 'vertical' : 'horizontal'}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}

          {layout === 'horizontal' ? (
            <>
              <XAxis
                dataKey={resolvedX}
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString('es-ES')}
              />
            </>
          ) : (
            <>
              <XAxis
                type="number"
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString('es-ES')}
              />
              <YAxis
                dataKey={resolvedX}
                type="category"
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                width={100}
              />
            </>
          )}

          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '12px' }}
            iconType="rect"
          />

          {/* Renderizar barras apiladas */}
          {resolvedYKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="stack"
              fill={colors[index % colors.length]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>

      {/* Info adicional */}
      <div className="mt-4 text-xs text-gray-500 text-center">
        Las barras apiladas muestran la composición de cada categoría
      </div>
    </div>
  )
}
